// T817 — URL reduction for everything the browser SDK sends.
//
// Pure (no `posthog-js` import) so it is unit-testable and shareable. The rule is the
// server one (`safeReferrer`, T38) widened to the SDK's automatic properties: a query
// string is where this app puts tokens (`reset-password?token=`, `verify-email?token=`,
// `unsubscribe?token=`) and where other sites put emails, so the query is dropped —
// except the `utm_*` campaign parameters, which are the only query keys anyone measures,
// and even those lose any value that carries an `@`. The fragment is always dropped.
//
// Which properties: every key matching URL_KEY, not a fixed list. The first version kept
// a list of six and missed `$session_entry_url` / `$session_entry_referrer`, which
// posthog-js attaches to EVERY event — an independent review reproduced a live reset
// token reaching PostHog through them (PR #1836). A new SDK property is covered by its
// name, not by someone remembering to add it here.

import { APP_LINK_BASE } from "~/config/hosts";

const INSTAGRAM_ATTRIBUTION = { utm_source: "instagram", utm_medium: "private_reply", utm_campaign: "guide" } as const;

function isInstagramSource(url: URL): boolean {
  return url.origin === APP_LINK_BASE && url.searchParams.getAll("ref").length === 1 && url.searchParams.get("ref") === "ig";
}

const KEPT_QUERY_PREFIX = "utm_";

/** Property keys whose string value is a URL, a referrer or a path. */
const URL_KEY = /url|referrer|pathname/i;

/**
 * Campaign properties the SDK extracts from the query into their own keys (`utm_content`,
 * `$session_entry_utm_content`, `$initial_utm_content`, …). A value carrying an `@` is an
 * email someone put in a campaign link: dropped, like it is from the URL itself.
 */
const UTM_KEY = /utm_/i;

/**
 * Member-id-shaped path segments on Back-Office paths (`/admin/users/<id>`): 16+ id
 * characters. Replaced by `:id` so the founder's own pageviews carry no member id.
 */
const ADMIN_ID_SEGMENT = /^[A-Za-z0-9_-]{16,}$/;

function sanitizePath(pathname: string): string {
  if (!pathname.startsWith("/admin")) return pathname;
  return pathname
    .split("/")
    .map((seg) => (ADMIN_ID_SEGMENT.test(seg) ? ":id" : seg))
    .join("/");
}

/** Reduce one URL to origin + pathname + `utm_*`. Non-URLs pass through unchanged
 * (`$direct`, empty), except that anything after `?`/`#` is cut. */
export function sanitizeAnalyticsUrl(value: unknown): unknown {
  if (typeof value !== "string" || value === "") return value;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return sanitizePath(value.split(/[?#]/)[0]);
  }
  const kept = new URLSearchParams();
  for (const [k, v] of url.searchParams) {
    if (k.toLowerCase().startsWith(KEPT_QUERY_PREFIX) && !v.includes("@")) kept.append(k, v);
  }
  // Decode the compact first-party source only inside consented SDK output.
  if (isInstagramSource(url)) {
    for (const [key, value] of Object.entries(INSTAGRAM_ATTRIBUTION)) kept.set(key, value);
  }
  const query = kept.toString();
  return `${url.origin}${sanitizePath(url.pathname)}${query ? `?${query}` : ""}`;
}

type Bag = Record<string, unknown> | undefined | null;

function sanitizeBag(bag: Bag): void {
  if (!bag) return;
  for (const key of Object.keys(bag)) {
    if (URL_KEY.test(key)) bag[key] = sanitizeAnalyticsUrl(bag[key]);
    else if (UTM_KEY.test(key) && typeof bag[key] === "string" && (bag[key] as string).includes("@")) {
      delete bag[key];
    }
  }
}

/**
 * `before_send` body: mutates and returns the event with every URL property reduced, in
 * the event's own properties and in the `$set` / `$set_once` person bags the SDK fills.
 */
export function sanitizeCapturedEvent<
  T extends { properties?: Bag; $set?: Bag; $set_once?: Bag } | null,
>(event: T): T {
  if (!event) return event;
  const currentUrl = event.properties?.$current_url;
  if (typeof currentUrl === "string") {
    try {
      if (isInstagramSource(new URL(currentUrl))) Object.assign(event.properties!, INSTAGRAM_ATTRIBUTION);
    } catch { /* Non-URL values are reduced by sanitizeBag below. */ }
  }
  sanitizeBag(event.properties);
  sanitizeBag(event.$set);
  sanitizeBag(event.$set_once);
  sanitizeBag(event.properties?.$set as Bag);
  sanitizeBag(event.properties?.$set_once as Bag);
  return event;
}
